import { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';

import { CloudFirmwareStatus, FirmwareUpdateStatus } from '../../features/ev-charger/types';

export type FirmwareUpdateUiMode = 'pending' | 'downloading' | 'installing' | 'success' | 'failed' | null;

export type GlobalUiMode =
  | 'offline'
  | 'available'
  | 'scheduled-active'
  | 'scheduled-waiting'
  | 'charging'
  | 'ev-suspended'
  | 'evse-suspended'
  | 'evse-smartamp-stopped'
  | 'evse-smartplug-stopped'
  | 'fw-pending'
  | 'fw-downloading'
  | 'fw-installing'
  | 'error';

export function deriveFirmwareUpdateUiMode(
  cloudFirmwareStatus: CloudFirmwareStatus | null,
  firmwareUpdateStatus: FirmwareUpdateStatus | null
): FirmwareUpdateUiMode {
  if (cloudFirmwareStatus === null) return null;

  if (cloudFirmwareStatus === 'Pending') return 'pending';
  if (cloudFirmwareStatus === 'Success') return 'success';
  if (cloudFirmwareStatus === 'Failed' || cloudFirmwareStatus === 'FailedChargerNotConnected') return 'failed';

  if (firmwareUpdateStatus === 'Downloading' || firmwareUpdateStatus === 'Downloaded') return 'downloading';
  if (firmwareUpdateStatus === 'DownloadFailed' || firmwareUpdateStatus === 'InstallationFailed') return 'failed';

  // Installing is the fallback while InProgress events are still being received.
  return 'installing';
}

export function deriveGlobalUiMode(
  status: ChargerStatus | undefined,
  isScheduledActive: boolean | null,
  isTimedScheduleEnabled: boolean,
  scheduledStart: string | null,
  scheduledEnd: string | null,
  isSmartAmpActive: boolean,
  isSmartPlugActive: boolean,
  cloudFirmwareStatus: CloudFirmwareStatus | null = null,
  firmwareUpdateStatus: FirmwareUpdateStatus | null = null
): GlobalUiMode {
  const firmwareMode = deriveFirmwareUpdateUiMode(cloudFirmwareStatus, firmwareUpdateStatus);

  if (firmwareMode === 'pending') return 'fw-pending';
  if (firmwareMode === 'downloading') return 'fw-downloading';
  if (firmwareMode === 'installing') return 'fw-installing';

  if (!status) return 'offline';
  if (status === 'Faulted') return 'error';
  // Actively charging must always be blue + session/speed/rate content,
  // even when isScheduledActive is true (Automatic charging scenario).
  if (status === 'Charging') return 'charging';
  if (status === 'SuspendedEV') return 'ev-suspended';
  // SuspendedEVSE + SmartPlug active: dedicated stopped-by-smartplug view (red, red indicator).
  if (status === 'SuspendedEVSE' && isSmartPlugActive) return 'evse-smartplug-stopped';
  // SuspendedEVSE + SmartAmp active: dedicated stopped-by-smartamp view (blue, red indicator).
  if (status === 'SuspendedEVSE' && isSmartAmpActive) return 'evse-smartamp-stopped';
  if (status === 'SuspendedEVSE' && (isTimedScheduleEnabled || isScheduledActive)) return 'scheduled-waiting';
  if (status === 'Finishing' && (scheduledStart || scheduledEnd)) return 'scheduled-waiting';
  if (isScheduledActive) return 'scheduled-active';
  switch (status) {
    case 'Finishing':
      return 'charging';
    case 'SuspendedEVSE':
      return 'evse-suspended';
    case 'Unavailable_Disconnect':
      return 'offline';
    case 'Available':
    case 'Preparing':
    case 'Unavailable_Restart':
    case 'Unavailable_Charger':
      return 'available';
    default:
      return 'offline';
  }
}
